import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { from } from 'rxjs';
import { ApConfig, ApVendor } from 'src/app/classes/models';
import { GridActionColumnActions } from 'src/app/interfaces/grid-action-column-actions';
import { GridActionColumnMap } from 'src/app/interfaces/grid-action-column-map';
import { ConfirmModalComponent } from 'src/app/modals/confirm-modal/confirm-modal.component';
import { ApConfigService } from 'src/app/services/ap-config.service';
import { ApVendorService } from 'src/app/services/ap-vendor.service';
import { ToastService } from 'src/app/services/toast.service';
import { apConfigsService } from 'src/app/shared/vive-api/ap-configs.service';
import { companiesService } from 'src/app/shared/vive-api/companies.service';
import { LOAD_BALANCER_TIMEOUT_MS } from 'src/app/shared/vive-api/vive-api.config';

import {
  getApVendorClientManagedColumnActions,
  isApVendorLinked
} from '../helpers/ap-vendor-grid-actions.helper';

@Component({
  selector: 'app-client-admin-accounting-system',
  templateUrl: './client-admin-accounting-system.component.html',
  styleUrls: ['./client-admin-accounting-system.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ClientAdminAccountingSystemComponent {
  private _cdr = inject(ChangeDetectorRef);

  constructor(
    private route: ActivatedRoute,
    private ApConfigService: ApConfigService,
    private ApVendorService: ApVendorService,
    private ToastService: ToastService,
    private ModalService: NgbModal
  ) {
    const paramMap = this.route.snapshot.paramMap;
    const ApConfigId = Number(paramMap.get('ApConfigId'));

    Object.assign(this.suppliedGridQueryData, { ApConfigId });

    this.ApConfigService.getApConfig({
      id: ApConfigId
    }).subscribe({
      next: (apConfig: ApConfig) => {
        this.apConfig = apConfig;

        if (apConfig.apSystem === 'Vantaca') {
          // AP Exempt is not available for Vantaca (misleading vs their UI).
          this.suppressedColumns = ['isExempt'];
          this.defaultHiddenColumns = [];
        } else {
          // Opt-in column: available in the tool panel, hidden by default.
          this.suppressedColumns = [];
          this.defaultHiddenColumns = ['isExempt', 'hasMetaData'];
        }

        this._cdr.markForCheck();
      }
    });
  }

  apConfig?: ApConfig;
  defaultHiddenColumns: string[] = [];
  suppressedColumns: string[] = [];
  reloadGrid: boolean = false;
  suppliedGridQueryData: { [key: string]: any } = {
    attributes: ['ApConfigId', 'vendorKey'],
    aliasedAttributes: ['viveStatus', 'hasMetaData'],
    ApConfig: {
      attributes: ['id', 'CompanyId', 'showExemptChange', 'v3CutOverDate']
    }
  };

  ColumnIdToColumnActions: GridActionColumnMap = {
    ['$actionColumn']: ['invite', 'refresh']
  };

  calculatedColumnActions = ({
    rowData
  }: {
    rowData: any;
  }): GridActionColumnActions[] | null => {
    const actions = getApVendorClientManagedColumnActions(
      rowData,
      this.apConfig?.apSystem
    );

    return actions.length > 0 ? actions : null;
  };

  calculateColumnTooltipMap = ({
    rowData
  }: {
    rowData: any;
  }): null | Partial<{ [key in GridActionColumnActions]: string }> => {
    const isV3 =
      this.apConfig?.apSystem === 'Vantaca' &&
      rowData.ApConfig?.v3CutOverDate &&
      rowData.ApConfig.v3CutOverDate <= new Date();

    return {
      add: isV3 ? 'Client Managed' : 'Add Exempt Flag',
      remove: isV3 ? 'VIVE Managed' : 'Remove Exempt Flag',
      invite: 'Invite Vendor',
      refresh: 'Refresh'
    };
  };

  syncAllVendorsNow(): void {
    if (!this.apConfig?.id) {
      return;
    }

    from(
      apConfigsService.syncAllVendors({
        pathParams: { apConfigId: this.apConfig.id },
        options: { requestTimeout: LOAD_BALANCER_TIMEOUT_MS }
      })
    ).subscribe({
      next: () => {
        this.reloadGrid = true;
        this.ToastService.addSuccessToast(
          'Success',
          'All linked vendors have been queued for sync.'
        );
      }
    });
  }

  action({
    action,
    rowData
  }: {
    action: GridActionColumnActions;
    rowData: ApVendor;
  }) {
    const { id, ApConfigId, vendorKey, InvitationContactId, ApConfig } =
      rowData;

    if (!id) {
      this.ToastService.addErrorToast('Error', 'No row ID found.', {
        returnError: true
      });
    }

    if (!ApConfig) {
      throw this.ToastService.addErrorToast(
        'Error',
        'No Ap Config data found for this vendor.',
        {
          returnError: true
        }
      );
    }

    const { CompanyId, v3CutOverDate } = ApConfig;

    const isV3 =
      this.apConfig?.apSystem === 'Vantaca' &&
      v3CutOverDate &&
      v3CutOverDate <= new Date();

    if (action === 'add' || action === 'remove') {
      if (this.apConfig?.apSystem !== 'Vantaca' || isApVendorLinked(rowData)) {
        return;
      }
    }

    if (action === 'add') {
      if (isV3) {
        from(
          companiesService.createApVendorMeta({
            pathParams: { companyId: CompanyId! },
            data: { ApConfigId: ApConfigId!, vendorKey: vendorKey! },
            options: { requestTimeout: LOAD_BALANCER_TIMEOUT_MS }
          })
        ).subscribe({
          next: () => {
            this.ToastService.addSuccessToast(
              'Success',
              'Successfully updated Ap Vendor.'
            );
            this.reloadGrid = true;
            this._cdr.markForCheck();
          }
        });
      } else {
        const apVendor = {
          id,
          isExempt: true
        };

        this.ApVendorService.saveApVendor(apVendor).subscribe({
          next: () => {
            this.ToastService.addSuccessToast(
              'Success',
              'Successfully added exempt flag.'
            );
            this.reloadGrid = true;
            this._cdr.markForCheck();
          }
        });
      }
    }

    if (action === 'remove') {
      if (isV3) {
        from(
          companiesService.deleteApVendorMetaByApConfigIdAndVendorKey({
            pathParams: {
              companyId: CompanyId!,
              apConfigId: ApConfigId!,
              vendorKey: vendorKey!
            },
            options: { requestTimeout: LOAD_BALANCER_TIMEOUT_MS }
          })
        ).subscribe({
          next: () => {
            this.ToastService.addSuccessToast(
              'Success',
              'Successfully updated Ap Vendor.'
            );
            this.reloadGrid = true;
            this._cdr.markForCheck();
          }
        });
      } else {
        const apVendor = {
          id,
          isExempt: false
        };

        this.ApVendorService.saveApVendor(apVendor).subscribe({
          next: () => {
            this.ToastService.addSuccessToast(
              'Success',
              'Successfully removed exempt flag.'
            );
            this.reloadGrid = true;
            this._cdr.markForCheck();
          }
        });
      }
    }

    if (action === 'invite') {
      if (InvitationContactId && InvitationContactId !== null) {
        const modalRef = this.ModalService.open(ConfirmModalComponent, {
          centered: true,
          backdrop: 'static'
        });

        Object.assign(modalRef.componentInstance, {
          displayText:
            'Invitation has already been sent. Are you sure you want to re-send invitation?'
        });

        modalRef.closed.subscribe({
          next: (confirm) => {
            if (confirm) {
              this.ApVendorService.inviteVendor(rowData).subscribe({
                next: () =>
                  this.ToastService.addSuccessToast(
                    'Success',
                    'Successfully resent vendor invitation.'
                  )
              });
            }
          }
        });
      } else {
        this.ApVendorService.inviteVendor(rowData).subscribe({
          next: () =>
            this.ToastService.addSuccessToast(
              'Success',
              'Successfully sent vendor invitation.'
            )
        });
      }
    }

    if (action === 'refresh') {
      this.reloadGrid = true;
      this._cdr.markForCheck();
    }
  }
}
